import { CheckboxButtonGroupValue } from "./Button";
import { CardElementName, getDefaultElementQuantityMap } from "./CardElement"
import { PublishState } from "./Common"
import { DateRange, getDateRange } from "./Date";
import { Deck, DeckState, getDefaultDeckState } from "./Deck"
import { ComboBoxOption } from "./Input";
import { TournamentFormat } from "./TournamentFormat"

export type TournamentSortBy = "sortDate" | "createdDate" | "updatedDate" | 'views' | "attendees";
export type TournamentDateRange = "All" | "2024 Season" | "Opus 22" | "Opus 23" | "Opus 24" | "Opus 25" | "2025 Season";

export const TOURNAMENT_TIER_MAP: Record<TournamentTierKey, TournamentTierName> = {
    "tier-10": "World",
    "tier-20": "National / Continental",
    "tier-30": "Regional",
    "tier-40": "Local Qualifier",
    "other": "Other"
}

export const TOURNAMENT_REGION_MAP: Record<TournamentRegionKey, TournamentRegionName> = {
    "region-100": "North America",
    "region-200": "Europe",
    "region-300": "South East Asia",
    "region-400": "East Asia",
    "region-500": "Oceania",
    "other": "Other"
}

export const TOURNAMENT_TIER_KEYS = ["tier-10", "tier-20", "tier-30", "tier-40", "other"] as const;
export const TOURNAMENT_TIER_NAMES = ["World", "National / Continental", "Regional", "Local Qualifier", "Other"] as const;
export type TournamentTierKey = typeof TOURNAMENT_TIER_KEYS[number];
export type TournamentTierName = typeof TOURNAMENT_TIER_NAMES[number];

export const TOURNAMENT_REGION_KEYS = ["region-100", "region-200", "region-300", "region-400", "region-500", "other"] as const;
export const TOURNAMENT_REGION_NAMES = ["North America", "Europe", "South East Asia", "East Asia", "Oceania", "Other"] as const;
export type TournamentRegionKey = typeof TOURNAMENT_REGION_KEYS[number];
export type TournamentRegionName = typeof TOURNAMENT_REGION_NAMES[number];

export function getTournamentTierOptions(): ComboBoxOption<TournamentTierKey>[] {
    return Object.entries(TOURNAMENT_TIER_MAP).map(([key, name]) => ({ label: name, value: key as TournamentTierKey }));
}

export function getTournamentTierCheckboxOptions(): CheckboxButtonGroupValue<TournamentTierKey>[] {
    return Object.entries(TOURNAMENT_TIER_MAP).map(([key, name]) => ({ label: name, value: key as TournamentTierKey }));
}

export function getTournamentTierSelection(key: TournamentTierKey | undefined): ComboBoxOption<TournamentTierKey> | null {
    if (!key) {
        return null;
    }

    return { label: TOURNAMENT_TIER_MAP[key] ?? TOURNAMENT_TIER_MAP["other"], value: key ?? "other" }
}

export function getTournamentRegionOptions(): ComboBoxOption<TournamentRegionKey>[] {
    return Object.entries(TOURNAMENT_REGION_MAP).map(([key, name]) => ({ label: name, value: key as TournamentRegionKey }));
}

export function getTournamentRegionSelection(key: TournamentRegionKey | undefined | null): ComboBoxOption<TournamentRegionKey> | null {
    if (!key) {
        return null;
    }

    return { label: TOURNAMENT_REGION_MAP[key] ?? TOURNAMENT_REGION_MAP["other"], value: key ?? "other" }
}

export const tournamentDateRangeMap: Record<TournamentDateRange, DateRange> = {
    "All": { start: null, end: null },
    "Opus 22": getDateRange("3/20/2024", "8/1/2024"),
    "Opus 23": getDateRange("8/2/2024", "11/14/2024"),
    "2024 Season": getDateRange("4/1/2024", "1/1/2025"),
    "Opus 24": getDateRange("11/15/2024", "3/28/2025"),
    "Opus 25": getDateRange("3/28/2025", "8/8/2025"),
    "2025 Season": getDateRange("04/01/2025", "1/1/2026")
}

export type TournamentDeckOverview = {
    id: string,
    rank: string,
    playerName: string,
    deckName: string,
    elements: Record<CardElementName, number>
}

export type TournamentOverview = {
    id: string,
    userId: string,
    state: PublishState,
    name: string,
    date: string,
    sortDate: number,
    attendees: number,
    format: TournamentFormat,
    tier: TournamentTierKey,
    region: TournamentRegionKey,
    numberOfDecks: number,
    deckOverviews: TournamentDeckOverview[],
    elements: Record<CardElementName, number>,
    serials: Record<string, number>,
    limitBreaks: Record<string, number> | undefined | null
    createdDate: number,
    updatedDate: number,
    views: number,
    description: string | undefined,
    bracket: TournamentBracket,
    likes?: string[],
    liked?: boolean,

    venueId: string,
    venueName: string,
    venueCountryId: string,
    venueCountry: string,
    venueProvinceId: string,
    venueProvince: string,
    venueCityId: string | null
    venueCity: string | null,
    venueRegionId: string
}

export type Tournament = {
    id: string,
    userId: string,
    state: PublishState,
    name: string,
    date: string,
    attendees: number,
    format: TournamentFormat,
    tier: TournamentTierKey,
    region: TournamentRegionKey,
    numberOfDecks: number,
    twoDeckFormat: boolean,
    decks: Deck[],
    venue: TournamentVenue | null,
    elements: Record<CardElementName, number>,
    serials: Record<string, number>,
    limitBreaks: Record<string, number>,
    createdDate: number,
    updatedDate: number,
    views: number,
    description: string | undefined,
    bracket: TournamentBracketState
}

export type TournamentState = {
    id: string,
    userId: string,
    state: PublishState,
    name: string,
    date: string,
    attendees: number,
    format: TournamentFormat,
    tier: TournamentTierKey | undefined,
    region: TournamentRegionKey | undefined,
    venue: TournamentVenue | undefined,
    numberOfDecks: number,
    twoDeckFormat: boolean,
    players: number,
    decks: DeckState[],
    elements: Record<CardElementName, number>,
    serials: Record<string, number>,
    limitBreaks: Record<string, number>,
    description: string | undefined,
    bracket: TournamentBracketState
}

export type TournamentVenue = {
    id: string,
    name: string,
    countryId: string,
    country: string,
    provinceId: string,
    province: string,
    cityId: string | null
    city: string | null,
    regionId: string
}

export type TournamentBracket = {
    rounds: TournamentRound[]
}

export type TournamentRound = {
    matches: TournamentMatch[]
}

export type TournamentMatch = {
    results: TournamentGameResult[],
}

export type TournamentGameResult = {
    player: string,
    win: boolean
    deck?: string
}

export type TournamentBracketState = {
    rounds: TournamentRoundState[],
    selectedIndex: number
}

export type TournamentRoundState = {
    matches: TournamentMatchState[],
    remainingPlayers: ComboBoxOption<number>[]
}

export type TournamentMatchState = {
    results: TournamentGameResultState[],
    isOpen: boolean
}

export type TournamentGameResultState = {
    player: ComboBoxOption<number> | null,
    win: boolean
    deck?: string
}

export const tournamentRankings = ["1", "2", "3-4", "3", "4", "5-8", "5", "6", "7", "8", "9-16", "9", "10", "11", "12", "13", "14", "15", "16", "17-32"] as const;
export type TournamentRanking = typeof tournamentRankings[number];

export function getRequiredPlayersPerRank(props: { ranking: TournamentRanking, isTwoDeckFormat: boolean }): number {
    const players = requiredPlayersPerRanking[props.ranking];
    return props.isTwoDeckFormat ? (players * 2) : players;
}

export const requiredPlayersPerRanking: Record<TournamentRanking, number> = {
    "1": 1,
    "2": 1,
    "3-4": 2,
    "3": 1,
    "4": 1,
    "5-8": 4,
    "5": 1,
    "6": 1,
    "7": 1,
    "8": 1,
    "9-16": 8,
    "9": 1,
    "10": 1,
    "11": 1,
    "12": 1,
    "13": 1,
    "14": 1,
    "15": 1,
    "16": 1,
    "17-32": 16
}

export const rankingSortMap: Record<TournamentRanking, number> = {
    "1": 1,
    "2": 2,
    "3-4": 3.5,
    "3": 3,
    "4": 4,
    "5-8": 8.5,
    "5": 5,
    "6": 6,
    "7": 7,
    "8": 8,
    "9-16": 16.5,
    "9": 9,
    "10": 10,
    "11": 11,
    "12": 12,
    "13": 13,
    "14": 14,
    "15": 15,
    "16": 16,
    "17-32": 32.5
}

export const invalidRankingsPerRanking: Record<TournamentRanking, TournamentRanking[]> = {
    "1": [],
    "2": [],
    "3-4": ["3", "4"],
    "3": [],
    "4": [],
    "5-8": ["5", "6", "7", "8"],
    "5": [],
    "6": [],
    "7": [],
    "8": [],
    "9-16": ["9", "10", "11", "12", "13", "14", "15", "16"],
    "9": [],
    "10": [],
    "11": [],
    "12": [],
    "13": [],
    "14": [],
    "15": [],
    "16": [],
    "17-32": []
}

export function getBlankTournamentRankingQuantityMap() {
    const assignedRankings: Record<TournamentRanking, number> = {
        "1": 0,
        "2": 0,
        "3-4": 0,
        "3": 0,
        "4": 0,
        "5-8": 0,
        "5": 0,
        "6": 0,
        "7": 0,
        "8": 0,
        "9-16": 0,
        "9": 0,
        "10": 0,
        "11": 0,
        "12": 0,
        "13": 0,
        "14": 0,
        "15": 0,
        "16": 0,
        "17-32": 0
    }

    return assignedRankings;
}

export const roundsByPlayerCount: Record<number, number> = {
    32: 5,
    16: 4,
    8: 3,
    4: 2,
    2: 1
};

export function getDefaultTournamentState(): TournamentState {
    const defaultState: TournamentState = {
        id: "",
        userId: "",
        state: "draft",
        attendees: 8,
        date: "",
        format: "Standard",
        name: "",
        tier: undefined,
        region: undefined,
        decks: [
            getDefaultDeckState(""),
            getDefaultDeckState(""),
            getDefaultDeckState(""),
            getDefaultDeckState(""),
            getDefaultDeckState(""),
            getDefaultDeckState(""),
            getDefaultDeckState(""),
            getDefaultDeckState("")
        ],
        numberOfDecks: 8,
        players: 8,
        twoDeckFormat: false,
        elements: getDefaultElementQuantityMap(),
        serials: {},
        limitBreaks: {},
        description: "",
        venue: undefined,
        bracket: {
            rounds: [],
            selectedIndex: 0
        }
    };

    defaultState.bracket = getDefaultBracket(defaultState);
    return defaultState;
}

function getDefaultBracket(tournament: TournamentState): TournamentBracketState {
    const rounds = roundsByPlayerCount[tournament.decks.length];
    const players: ComboBoxOption<number>[] = tournament.decks.map((deck, deckIndex) => {
        return {
            label: deck.editableFields.unsaved.playerName
                ? deck.editableFields.unsaved.playerName
                : `Deck #${deckIndex + 1} Player`,
            value: deckIndex
        }
    });

    const bracket: TournamentBracketState = {
        rounds: [],
        selectedIndex: 0
    };

    Array.from(Array(rounds).keys()).forEach((roundIndex) => {
        const numberOfMatches = Math.pow(2, roundIndex + 1) / 2;
        const round: TournamentRoundState = {
            matches: [],
            remainingPlayers: players
        };

        Array.from(Array(numberOfMatches).keys()).forEach(() => {
            const match: TournamentMatchState = {
                isOpen: false,
                results: [
                    {
                        player: null,
                        win: false
                    },
                    {
                        player: null,
                        win: false
                    }
                ]
            }

            round.matches.push(match);
        });

        bracket.rounds.push(round);
    });

    return bracket;
}